// ** React Imports
import React from 'react'
import ReactDOM from 'react-dom/client'

// ** Third Party Imports
import ReactGA from 'react-ga4'
import * as Sentry from '@sentry/react'
import 'react-toastify/dist/ReactToastify.min.css'
import 'katex/dist/katex.min.css'
import { Provider as ReduxProvider } from 'react-redux'
import {
	matchRoutes,
	useLocation,
	useNavigationType,
	BrowserRouter as Router,
	createRoutesFromChildren
} from 'react-router-dom'

// ** Redux Store
import { store } from '@/app/config/store'

// ** Component Imports
// Direct paths (not the '@/shared/ui' barrel) so the entry chunk doesn't pull
// in every shared component and its libraries (charts, forms, ...)
import { ReactToast } from '@/shared/ui/elements/ReactToast'
import { ScrollToTop } from '@/shared/ui/ScrollToTop'

// ** Utility Imports
import { env } from '@/shared/lib'

// ** Style Imports
import { GlobalStyle } from './styles'

// ** Providers
import { AppRoutes } from './config/routes'
import { AuthWrapper } from '@/entities/session'
import { ThemeProvider } from './providers'
import { GAListener } from '@/shared/lib/GAListener'
import { whenPageIdle } from '@/shared/lib/whenPageIdle'

Sentry.init({
	// Disable Sentry in development mode
	dsn: import.meta.env.MODE === 'development' ? undefined : env.sentryDsnKey,
	environment: import.meta.env.MODE,
	integrations: [
		// See docs for support of different versions of variation of react router
		// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
		Sentry.reactRouterV6BrowserTracingIntegration({
			useEffect: React.useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes
		})
		// Session replay is added once the page is idle, see below
	],

	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for performance monitoring.
	tracesSampleRate: 1.0,

	// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: ['localhost', env.apiUrl],

	// Capture Replay for 10% of all sessions,
	// plus for 100% of sessions with an error
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0
})

// Non-critical scripts load once the page is idle (or on first interaction)
// so they don't delay the first render. Error reporting above stays eager.
whenPageIdle(() => {
	// Lazy chunk (~300 KB); sample rates come from the Sentry.init options
	import('@sentry/replay')
		.then(({ replayIntegration }) => {
			Sentry.getCurrentHub().getClient()?.addIntegration?.(replayIntegration())
		})
		.catch(() => {})
})

// Pageviews sent before this runs are queued by GAListener
whenPageIdle(() => {
	ReactGA.initialize(env.googleTrackingId)
})

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
	<React.StrictMode>
		<Sentry.ErrorBoundary>
			<ReduxProvider store={store}>
				<ThemeProvider>
					<GlobalStyle />
					<ReactToast />
					<AuthWrapper>
						<Router>
							<GAListener />
							<ScrollToTop />
							<AppRoutes />
						</Router>
					</AuthWrapper>
				</ThemeProvider>
			</ReduxProvider>
		</Sentry.ErrorBoundary>
	</React.StrictMode>
)
